import { initializeGoogleAuth, googleConfig } from "./googleConfig";
import AuthManager from "src/auth/AuthManager";

export class GoogleAuthManager {
  private tokenClient: any = null;
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private oneTapCallback: ((response: any) => void) | null = null;
  private authManager: AuthManager;
  constructor(authManager: AuthManager) {
    this.authManager = authManager;
    this.initializeTokenClient();
  }

  private async initializeTokenClient() {
    await initializeGoogleAuth((response: any) => {
      this.handleTokenResponse(response);
    });

    // Initialize OAuth2 token client for traditional popup
    this.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: googleConfig.clientId,
      scope: googleConfig.scopes.join(" "),
      callback: (tokenResponse: any) => {
        this.handleTokenResponse(tokenResponse);
      },
    });

    // Set up One Tap callback
    this.oneTapCallback = (response: any) => {
      this.handleOneTapResponse(response);
    };
  }

  private handleTokenResponse(tokenResponse: any) {
    this.accessToken = tokenResponse.credential;
    this.idToken = tokenResponse.credential;

    // Decode the ID token to get user information
    const payload = this.decodeJwt(tokenResponse.credential);

    this.authManager.token = tokenResponse.credential;
    this.authManager.isAuthenticated = true;
    this.authManager.type = "google";
    this.authManager.providerLoginResult = tokenResponse;
    this.authManager.name = payload.name || null;
    this.authManager.email = payload.email || null;

    this.authManager.notifyListeners();
  }

  private handleOneTapResponse(response: any) {
    if (response.credential) {
      // One Tap provides an ID token directly
      this.idToken = response.credential;
      const payload = this.decodeJwt(response.credential);

      // For One Tap, we need to exchange the ID token for an access token
      this.exchangeIdTokenForAccessToken(response.credential);
    }
  }

  private async exchangeIdTokenForAccessToken(idToken: string) {
    try {
      // Exchange ID token for access token using Google's token endpoint
      const response = await fetch("https://oauth2.googleapis.com/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          grant_type: "urn:ietf:params:oauth:grant-type:jwt-bearer",
          assertion: idToken,
          client_id: googleConfig.clientId,
        }),
      });

      const tokenData = await response.json();

      if (tokenData.access_token) {
        this.accessToken = tokenData.access_token;
        const payload = this.decodeJwt(idToken);

        this.authManager.token = tokenData.access_token;
        this.authManager.isAuthenticated = true;
        this.authManager.type = "google";
        this.authManager.providerLoginResult = {
          ...tokenData,
          id_token: idToken,
        };
        this.authManager.name = payload.name || null;
        this.authManager.email = payload.email || null;

        this.authManager.notifyListeners();
      }
    } catch (error) {
      console.error("Error exchanging ID token for access token:", error);
      // Fallback to traditional OAuth flow
      this.tokenClient.requestAccessToken();
    }
  }

  private decodeJwt(token: string) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return {};
    }
  }

  public async signOut(): Promise<void> {
    this.clearTokens();
    this.authManager.token = null;
    this.authManager.type = "google";
    this.authManager.providerLoginResult = null;
    this.authManager.isAuthenticated = false;
    this.authManager.name = null;
    this.authManager.email = null;

    sessionStorage.removeItem("auth");

    // Revoke Google tokens
    if (this.accessToken) {
      window.google.accounts.oauth2.revoke(this.accessToken);
    }

    // Cancel One Tap
    window.google.accounts.id.cancel();

    this.authManager.notifyListeners();
    this.authManager.logoutCallback();
  }

  public async signIn(): Promise<boolean> {
    try {
      this.clearTokens();
      this.authManager.token = null;
      this.authManager.isAuthenticated = false;
      this.authManager.type = "google";
      this.authManager.providerLoginResult = null;
      this.promptOneTap();
      return true;
    } catch (error) {
      console.error("Google sign-in error:", error);
      return false;
    }
  }

  public async signInWithPopup(): Promise<boolean> {
    try {
      this.clearTokens();
      this.authManager.token = null;
      this.authManager.isAuthenticated = false;
      this.authManager.type = "google";
      this.authManager.providerLoginResult = null;

      this.tokenClient.requestAccessToken();

      return true;
    } catch (error) {
      console.error("Google popup sign-in error:", error);
      return false;
    }
  }

  private promptOneTap() {
    // Show One Tap sign-in (small, modern prompt)
    window.google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // One Tap not available, fall back to traditional popup
        this.tokenClient.requestAccessToken();
      }
    });
  }

  public async trySignInSilently(): Promise<boolean> {
    try {
      // Check if we have stored tokens in session storage
      const stored = sessionStorage.getItem("google_auth");
      if (stored && stored !== null) {
        const data = JSON.parse(stored);
        if (data.accessToken && data.idToken) {
          this.accessToken = data.accessToken;
          this.idToken = data.idToken;

          // Verify token is still valid
          const payload = this.decodeJwt(data.idToken);
          const currentTime = Math.floor(Date.now() / 1000);

          if (payload.exp && payload.exp > currentTime) {
            this.authManager.token = data.accessToken;
            this.authManager.isAuthenticated = true;
            this.authManager.type = "google";
            this.authManager.providerLoginResult = data;
            this.authManager.name = payload.name || null;
            this.authManager.email = payload.email || null;

            this.authManager.notifyListeners();
            return true;
          } else {
            // Token is expired, clear it
            this.clearTokens();
          }
        }
      }

      return false;
    } catch (error) {
      console.error("Google silent login error:", error);
      return false;
    }
  }

  public async signInSilent(): Promise<void> {
    const success = await this.trySignInSilently();
    if (!success) {
      // If silent login fails, try One Tap
      this.promptOneTap();
    }
  }

  protected async fetchTokens(): Promise<void> {
    // Tokens are fetched during sign-in process
    // This method is called by the abstract class but not used in Google implementation
  }

  protected clearTokens(): void {
    this.accessToken = null;
    this.idToken = null;
    sessionStorage.removeItem("google_auth");
  }

  // Helper method to store tokens in session storage
  private storeTokens(accessToken: string, idToken: string) {
    sessionStorage.setItem(
      "google_auth",
      JSON.stringify({
        accessToken,
        idToken,
        timestamp: Date.now(),
      })
    );
  }
}
